"use client";

import { getSession, signOut } from "next-auth/react";
import { inBrowser } from "./inBrowser";

interface TokenCacheEntry {
  token: string;
  expiration: number;
}

const TOKEN_CACHE_KEY = "_auth_token_cache";

/**
 * Set for the duration of a sign-out. While it is set the cache neither serves
 * a token nor accepts one.
 *
 * Clearing the cache before signOut() is not enough on its own. Every Apollo
 * request runs getAccessTokenResult() through GqlClient's authLink, and until
 * the session cookie is actually gone getSession() still answers with the old
 * idToken — so a request in flight during the sign-out writes that token
 * straight back into the entry that was just removed. SessionProvider is
 * mounted with `refetchInterval={60}` and `refetchOnWindowFocus`, so there is
 * reliably traffic in that window. This is the EN-6169 leak the explicit clear
 * was meant to close, reopened a few milliseconds later.
 */
let signingOut = false;

/**
 * Treat a token as stale this long before it actually expires, so a request
 * never goes out holding one that lapses in flight.
 */
const REFRESH_AHEAD_MS = 60 * 1000;

/** Cache window when the token carries no readable `exp`. */
const FALLBACK_TTL_MS = 10 * 1000;

/**
 * Hard ceiling on how long a token is reused, regardless of its own expiry.
 *
 * The cache is keyed globally, not per user, and it cannot be keyed per user
 * without reading the session on every call — which is the round trip the
 * cache exists to avoid. So the entry is bounded instead: this caps how long a
 * token can outlive the session that produced it if an explicit clear is ever
 * missed.
 *
 * Five minutes still removes the thrash this PR is about — the old ten-second
 * window meant nearly every caller re-fetched the session — while keeping the
 * worst-case staleness to minutes rather than the token's full hour.
 */
const MAX_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * How many times to re-ask for the token when the visitor is signed in but no
 * token has arrived yet. This is the race that made ~28% of authenticated
 * queries fail: the session resolves before its idToken does, and the caller
 * asked in between.
 */
const TOKEN_RETRY_ATTEMPTS = 2;
const TOKEN_RETRY_DELAY_MS = 150;

/**
 * What happened when we asked for an access token.
 *
 * The distinction that matters is `anonymous` vs `unavailable`. Both mean "no
 * token", but only the first is a legitimate state to send a request in — a
 * signed-out visitor reading public data. `unavailable` means the visitor *is*
 * signed in and the token simply is not here, so any request sent would be
 * rejected by the API as Unauthorized. Collapsing the two is what produced
 * requests with a blank Authorization header.
 */
export type AccessTokenResult =
  | { status: "token"; token: string }
  | { status: "anonymous" }
  | { status: "unavailable" };

/**
 * Exception thrown when credentials are invalid
 */
export class InvalidCredentialsException extends Error {
  constructor(public readonly error: string) {
    super("Invalid credentials");
  }
}

export function isInvalidCredentialsException(
  error: unknown,
): error is InvalidCredentialsException {
  return error instanceof InvalidCredentialsException;
}

/**
 * Gets a yardzen access token from session cache or next-auth route.
 *
 * redirects to login page if credentials are invalid (refresh token expired or revoked)
 *
 * @throws InvalidCredentialsException if credentials are invalid
 */
export async function getAccessToken(): Promise<string | undefined> {
  const result = await getAccessTokenResult();
  return result.status === "token" ? result.token : undefined;
}

/**
 * Same lookup as getAccessToken, but reports *why* there is no token so the
 * caller can tell a signed-out visitor from a signed-in one whose token has
 * not arrived. Retries a bounded number of times in the latter case, since the
 * token usually lands moments later.
 *
 * @throws InvalidCredentialsException if credentials are invalid
 */
export async function getAccessTokenResult(): Promise<AccessTokenResult> {
  const cached = getCachedToken();
  if (cached) {
    return { status: "token", token: cached };
  }

  for (let attempt = 0; ; attempt++) {
    const result = await getTokenFromAuth();

    if (result.status !== "unavailable" || attempt >= TOKEN_RETRY_ATTEMPTS) {
      return result;
    }

    await new Promise((resolve) => setTimeout(resolve, TOKEN_RETRY_DELAY_MS));
  }
}

async function getTokenFromAuth(): Promise<AccessTokenResult> {
  const auth = (await getSession()) as {
    idToken?: string;
    error?: string;
    user?: unknown;
  } | null;
  if (!inBrowser()) {
    return { status: "anonymous" };
  }
  if (auth?.error) {
    throw new InvalidCredentialsException(auth?.error);
  }
  if (auth?.idToken) {
    setCachedToken(auth.idToken);
    return { status: "token", token: auth.idToken };
  }

  // The session no longer carries a token, so any entry still sitting in the
  // cache belongs to a session that is gone — a sign-out, or a switch to a
  // different user. Drop it rather than let it be served until it lapses.
  // Explicit clears at the sign-out call sites make this immediate; this is
  // the backstop for the ones that are missed.
  clearTokenCache();

  // A session carrying a user but no idToken is the race worth reporting: the
  // visitor is signed in, so a request sent now is guaranteed a 401. No
  // session at all is an ordinary signed-out visitor.
  return auth?.user ? { status: "unavailable" } : { status: "anonymous" };
}

/**
 * The token's own expiry, in epoch ms, read from the JWT payload.
 *
 * Decoded without verifying the signature on purpose: this only schedules when
 * to fetch a fresh token. Whether the token is actually trusted is the API's
 * decision, and nothing here grants access.
 */
function tokenExpiresAt(token: string): number | undefined {
  const payload = token.split(".")[1];
  if (!payload) {
    return undefined;
  }

  try {
    // JWTs are base64url; atob expects standard base64.
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: unknown };

    return typeof decoded.exp === "number" ? decoded.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

function setCachedToken(token: string) {
  // The session is on its way out; whatever getSession() just answered with
  // belongs to it and must not outlive it.
  if (signingOut) {
    return;
  }

  // Previously a flat 10 seconds, on a token that lives about an hour. That
  // meant nearly every caller missed the cache and triggered a fresh
  // getSession() round trip, widening the window in which no token is
  // available — the direct cause of the blank-Authorization requests.
  const expiresAt = tokenExpiresAt(token);
  const ownExpiry = expiresAt
    ? expiresAt - REFRESH_AHEAD_MS
    : Date.now() + FALLBACK_TTL_MS;

  // Whichever comes first: the token's own expiry, or the staleness ceiling.
  const expiration = Math.min(ownExpiry, Date.now() + MAX_CACHE_TTL_MS);

  // Already inside the refresh window: don't cache, so the next caller fetches
  // a fresh token rather than reusing one about to lapse.
  if (expiration <= Date.now()) {
    return;
  }

  const cache: TokenCacheEntry = { token, expiration };

  window.sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(cache));
}

/**
 * next-auth's `signOut`, with the access-token cache cleared first.
 *
 * Prefer this over calling `signOut()` directly. The cache is keyed globally
 * rather than per user — it cannot be keyed per user without reading the
 * session on every call, which is the round trip the cache exists to avoid —
 * so a token left behind by a sign-out would otherwise be served to whoever
 * signs in next, until the entry lapses.
 *
 * getTokenFromAuth clears it as a backstop when it sees a session without a
 * token, and MAX_CACHE_TTL_MS bounds the worst case. Clearing here is what
 * makes it immediate.
 */
export async function signOutAndClearTokenCache(
  options?: Parameters<typeof signOut>[0],
) {
  signingOut = true;
  clearTokenCache();

  try {
    return await signOut(options);
  } finally {
    // Belt and braces. With the default `redirect: true` the page navigates
    // and this module is torn down anyway; with `redirect: false` it lives on,
    // so leave the cache empty and the flag down for whoever signs in next.
    clearTokenCache();
    signingOut = false;
  }
}

export function clearTokenCache() {
  // Never throw. This runs first in signOutAndClearTokenCache, and before
  // signIn at the login call sites, so an unavailable sessionStorage (private
  // mode, blocked site data, a server render) would otherwise take the
  // sign-out or sign-in down with it.
  try {
    window.sessionStorage.removeItem(TOKEN_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing token cache", error);
  }
}

function getCachedToken(): string | undefined {
  if (signingOut) {
    return undefined;
  }

  let cache: TokenCacheEntry;

  try {
    const _cache = window.sessionStorage.getItem(TOKEN_CACHE_KEY);
    if (!_cache) {
      return undefined;
    }

    cache = JSON.parse(_cache);
  } catch (error) {
    console.error("Error parsing token cache", error);
    window.sessionStorage.removeItem(TOKEN_CACHE_KEY);
    return undefined;
  }

  if (cache.expiration > Date.now()) {
    return cache.token;
  } else {
    window.sessionStorage.removeItem(TOKEN_CACHE_KEY);
  }

  return undefined;
}
